/**
 * Door sections — the group-wide open/shut flip with its occupancy gate, and the internal
 * whole-`statuses` restore that never re-runs it.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { hasStatus, objectGroupMembers, type CombatantId, type StatusInstance } from "../types";
import { worldFor } from "../world";
import { changed, requireCombatant, withCombatant, type EncounterHandler } from "./context";

export const applySetDoorState: EncounterHandler<"set_door_state"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.unit);
  if (!cR.ok) return cR;
  const target = cR.value;
  if (target.base.objectProfile?.door !== true) {
    return err(new CommandError(`set_door_state: combatant ${target.id} is not a door`));
  }
  const groupId = target.objectGroupId;
  const group = groupId ? objectGroupMembers(state, groupId) : [target];
  const live = group.filter((c) => !hasStatus(c.statuses, "Destroyed"));
  if (live.length === 0) {
    return err(
      new CommandError(
        `set_door_state: door ${groupId ?? target.id} is entirely destroyed (rubble has no door)`,
      ),
    );
  }
  // Idempotent-reject (mirrors apply_status's duplicate-name reject): if every
  // live section already carries the requested open state, there is nothing to do.
  if (live.every((c) => hasStatus(c.statuses, "Open") === cmd.open)) {
    return err(
      new CommandError(
        `set_door_state: door ${groupId ?? target.id} is already ${cmd.open ? "open" : "shut"}`,
      ),
    );
  }
  // Shutting: reject if any OTHER placed combatant's footprint overlaps ANY live
  // section's footprint — an open door's cell is free (the movement
  // gate above skips it entirely), so a unit may legitimately be standing in the
  // doorway. Naming the blocked section + cell is safe: this verb is GM-only and
  // the GM is omniscient, so fog's never-name-a-concealed-blocker convention
  // doesn't apply here.
  if (!cmd.open) {
    const world = worldFor(state);
    const memberIds = new Set(group.map((c) => c.id));
    for (const section of live) {
      if (!section.pos) continue;
      const sectionKeys = new Set(
        world.footprint(section.pos, section.base.size).cells().map((p) => world.key(p)),
      );
      for (const other of Object.values(state.combatants)) {
        if (memberIds.has(other.id) || !other.pos) continue;
        const overlaps = world
          .footprint(other.pos, other.base.size)
          .cells()
          .some((p) => sectionKeys.has(world.key(p)));
        if (overlaps) {
          return err(
            new CommandError(
              `set_door_state: cannot shut — combatant ${other.id} occupies section ${section.id} ` +
                `at ${world.key(section.pos)}`,
            ),
          );
        }
      }
    }
  }
  const priorStatuses: Record<CombatantId, StatusInstance[]> = {};
  let next = state;
  for (const section of live) {
    priorStatuses[section.id] = section.statuses;
    const has = hasStatus(section.statuses, "Open");
    if (has === cmd.open) continue; // this section already matches individually
    const statuses = cmd.open
      ? [...section.statuses, { name: "Open", expiry: { kind: "until_removed" as const } }]
      : section.statuses.filter((s) => s.name !== "Open");
    next = withCombatant(next, { ...section, statuses });
  }
  return ok({
    state: next,
    inverse: { type: "restore_door_state", actorId: cmd.actorId, statuses: priorStatuses },
    events: changed(null),
  });
};

export const applyRestoreDoorState: EncounterHandler<"restore_door_state"> = (state, cmd) => {
  // Internal inverse of set_door_state: restore each named combatant's whole
  // `statuses` array directly — unvalidated, and deliberately never re-runs the
  // shut-occupancy gate (which could now reject on a state that's moved on since
  // the forward apply). Its own inverse captures the pre-restore statuses so redo
  // round-trips too (the same discipline as `restore_pos`/`restore_combatant_base`).
  const priorForRedo: Record<CombatantId, StatusInstance[]> = {};
  let next = state;
  for (const [id, statuses] of Object.entries(cmd.statuses)) {
    const c = state.combatants[id];
    if (!c) continue; // defensive: a since-removed combatant, nothing to restore
    priorForRedo[id] = c.statuses;
    next = withCombatant(next, { ...c, statuses });
  }
  return ok({
    state: next,
    inverse: { type: "restore_door_state", actorId: cmd.actorId, statuses: priorForRedo },
    events: changed(null),
  });
};
